/**
 * Templates de procédures juridiques (Lot 6 Phase L2).
 *
 * Chaque template décrit un dossier juridique structuré (URSSAF, DREAL,
 * prud'hommes, douanes, litige client, concurrence déloyale…) qui peut
 * être instancié par `legalProceduresSlice.maybeOpenProcedure()` selon
 * des conditions de contexte (effectif, contrats Export, licenciements
 * récents, etc.).
 *
 * Différence avec `relationEventTemplates` (Phase 3) :
 *   - Émis par une autorité (URSSAF / DREAL / Tribunal / Douanes) et non
 *     un dirigeant concurrent.
 *   - Multiplicateurs financiers atténués par Juliette (Phase L1) via
 *     `getLegalFineMultiplier`.
 *   - Toujours kind='decision' avec 3 choix : amiable / négocier (Juliette)
 *     / contester (jury aléatoire).
 *   - Métadonnée `legalProcedure: { type, exposureMin, exposureMax, deadlineDays }`.
 *
 * 17/06/2026 — Création.
 */

import { getLegalFineMultiplier, hasLegalCounsel } from '../utils/legalSupport';

/* ─── Helpers de construction ─────────────────────────────────────────── */

/** Renvoie un montant arrondi à 100 € près dans [min, max] (déterministe via rng). */
function pickAmount(min, max, rng = Math.random) {
  return Math.round((min + rng() * (max - min)) / 100) * 100;
}

/**
 * Construit un message de procédure juridique standard.
 *
 * @param {Object} opts
 * @param {string} opts.type — id du template (urssaf / dreal / ...).
 * @param {string} opts.authority — autorité émettrice (URSSAF / DREAL / etc.).
 * @param {string} opts.subject — titre du message.
 * @param {string} opts.body — corps détaillé (Markdown supporté).
 * @param {Array<Object>} opts.choices — 3 choix structurés.
 * @param {Object} opts.metadata — { exposureMin, exposureMax, deadlineDays }
 */
function buildLegalMessage({ type, authority, subject, body, choices, metadata }) {
  return {
    kind: 'decision',
    senderType: 'legal',
    senderName: authority,
    senderRole: 'Autorité administrative',
    senderCompany: authority,
    senderAvatar: '#7C2D12', // brun-rouge officiel
    tone: 'formal',
    subject,
    body,
    choices,
    ttlDays: metadata?.deadlineDays || 30,
    payload: { legalProcedure: { type, ...(metadata || {}) } },
  };
}

/* ─── Template URSSAF — Contrôle déclaratif ────────────────────────────── */

const TPL_URSSAF = {
  id: 'legal-urssaf-audit',
  type: 'urssaf_audit',
  cooldownDays: 540,
  /**
   * Probabilité montée par la masse salariale (≥ 6 chauffeurs).
   * Calcul : 0.005 par jour, gated par effectif.
   */
  probability: 0.005,
  condition: (state) => (state.drivers || []).length >= 6,
  build: (state, rng = Math.random) => {
    const fineMult = getLegalFineMultiplier(state);
    const exposureMin = Math.round(4000 * fineMult);
    const exposureMax = Math.round(15000 * fineMult);
    const amicableCost = pickAmount(exposureMin, Math.round(exposureMin * 1.4), rng);
    const ignoreCost = pickAmount(exposureMax, Math.round(exposureMax * 1.3), rng);
    return buildLegalMessage({
      type: 'urssaf_audit',
      authority: 'URSSAF',
      subject: 'Contrôle URSSAF déclaratif — Convocation',
      body: `Madame, Monsieur,\n\nL'URSSAF de votre région a entamé un **contrôle déclaratif** sur vos déclarations sociales des 12 derniers mois (DSN, cotisations patronales, taxes salariales).\n\nL'examen porte notamment sur :\n• La conformité des DSN mensuelles\n• Le calcul du taux d'AT/MP (accidents du travail)\n• Les déclarations des heures supplémentaires conducteurs\n\n**Exposition estimée :** ${exposureMin.toLocaleString('fr-FR')} – ${exposureMax.toLocaleString('fr-FR')} € selon l'issue.\n**Délai de réponse :** 30 jours.\n\nVos options sont détaillées ci-dessous.\n\n— L'Inspecteur du Recouvrement`,
      choices: [
        {
          id: 'amicable',
          label: hasLegalCounsel(state)
            ? `Régularisation amiable (-${amicableCost.toLocaleString('fr-FR')} € · Juliette)`
            : `Régularisation amiable (-${amicableCost.toLocaleString('fr-FR')} €)`,
          tooltip: 'Dossier classé. Pas d\'impact réputationnel.',
          effects: [{ kind: 'treasury', amount: -amicableCost }],
        },
        {
          id: 'negotiate',
          label: hasLegalCounsel(state)
            ? `Négocier (médiateur · Juliette)`
            : `Négocier (médiateur)`,
          tooltip: hasLegalCounsel(state)
            ? `Juliette obtient ~30 % de réduction. Coût ${Math.round(amicableCost * 0.7).toLocaleString('fr-FR')} €.`
            : 'Médiation incertaine — coût variable.',
          effects: [
            {
              kind: 'treasury',
              amount: hasLegalCounsel(state) ? -Math.round(amicableCost * 0.7) : -amicableCost,
            },
          ],
        },
        {
          id: 'ignore',
          label: `Ignorer (-${ignoreCost.toLocaleString('fr-FR')} € + intérêts)`,
          tooltip: 'Mise en recouvrement automatique. -3 réputation.',
          effects: [
            { kind: 'treasury', amount: -ignoreCost },
            { kind: 'reputation', amount: -3 },
          ],
        },
      ],
      metadata: { exposureMin, exposureMax, deadlineDays: 30 },
    });
  },
};

/* ─── Template DREAL — Audit renforcé ──────────────────────────────────── */

const TPL_DREAL = {
  id: 'legal-dreal-audit',
  type: 'dreal_audit',
  cooldownDays: 720,
  probability: 0.003,
  condition: (state) => (state.vehicles || []).length >= 8,
  build: (state, rng = Math.random) => {
    const fineMult = getLegalFineMultiplier(state);
    const exposureMin = Math.round(3000 * fineMult);
    const exposureMax = Math.round(12000 * fineMult);
    const amicableCost = pickAmount(exposureMin, exposureMin + 2000, rng);
    const ignoreCost = pickAmount(exposureMax, exposureMax + 3000, rng);
    return buildLegalMessage({
      type: 'dreal_audit',
      authority: 'DREAL',
      subject: 'Audit DREAL renforcé — Convocation',
      body: `Madame, Monsieur,\n\nLa Direction Régionale de l'Environnement, de l'Aménagement et du Logement (**DREAL**) procède à un **audit renforcé** de votre flotte au titre du contrôle TRM (Transport Routier de Marchandises).\n\nPoints examinés :\n• Cartes conducteurs et chronotachygraphes\n• Conformité des contrôles techniques\n• Respect des temps de conduite (Règlement CE 561/2006)\n• Habilitations FIMO/FCO\n\n**Exposition :** ${exposureMin.toLocaleString('fr-FR')} – ${exposureMax.toLocaleString('fr-FR')} €.\n**Délai :** 21 jours.\n\nUne réponse rapide et documentée évite la majoration.\n\n— L'Inspecteur DREAL`,
      choices: [
        {
          id: 'amicable',
          label: `Mise en conformité (-${amicableCost.toLocaleString('fr-FR')} €)`,
          tooltip: 'Documents fournis sous 21 jours. Dossier clos.',
          effects: [{ kind: 'treasury', amount: -amicableCost }],
        },
        {
          id: 'negotiate',
          label: hasLegalCounsel(state) ? `Recours administratif (Juliette)` : `Recours administratif`,
          tooltip: hasLegalCounsel(state)
            ? `Juliette plaide auprès du tribunal administratif. ${Math.round(amicableCost * 0.6).toLocaleString('fr-FR')} €.`
            : 'Recours risqué sans avocat.',
          effects: [
            {
              kind: 'treasury',
              amount: hasLegalCounsel(state) ? -Math.round(amicableCost * 0.6) : -Math.round(amicableCost * 1.1),
            },
          ],
        },
        {
          id: 'ignore',
          label: `Ignorer (-${ignoreCost.toLocaleString('fr-FR')} € · saisie)`,
          tooltip: 'Saisie administrative + -4 réputation.',
          effects: [
            { kind: 'treasury', amount: -ignoreCost },
            { kind: 'reputation', amount: -4 },
          ],
        },
      ],
      metadata: { exposureMin, exposureMax, deadlineDays: 21 },
    });
  },
};

/* ─── Template Prud'hommes — Litige post-licenciement ──────────────────── */

const TPL_PRUDHOMMES = {
  id: 'legal-prudhommes',
  type: 'prudhommes',
  cooldownDays: 360,
  probability: 0.01,
  condition: (state) => Object.keys(state.firedDriversArchive || {}).length >= 2,
  build: (state, rng = Math.random) => {
    const fineMult = getLegalFineMultiplier(state);
    const exposureMin = Math.round(5000 * fineMult);
    const exposureMax = Math.round(22000 * fineMult);
    const amicableCost = pickAmount(exposureMin, Math.round(exposureMin * 1.2), rng);
    const ignoreCost = pickAmount(exposureMax, Math.round(exposureMax * 1.1), rng);
    return buildLegalMessage({
      type: 'prudhommes',
      authority: 'Conseil de Prud\'hommes',
      subject: 'Convocation Prud\'hommes — Procédure aux fins de conciliation',
      body: `Maître,\n\nVous êtes informé que le **Conseil de Prud'hommes** de votre ressort a enregistré une demande à votre encontre, déposée par un ancien salarié contestant les modalités de sa rupture de contrat.\n\nMotifs invoqués :\n• Licenciement sans cause réelle et sérieuse\n• Manquement à la procédure (art. L1232-2 du Code du travail)\n• Demande d'indemnité de licenciement majorée\n\n**Exposition :** ${exposureMin.toLocaleString('fr-FR')} – ${exposureMax.toLocaleString('fr-FR')} €.\n**Audience de conciliation :** sous 60 jours.\n\nLa phase de conciliation permet souvent d'aboutir à un accord transactionnel.\n\n— Le Greffier`,
      choices: [
        {
          id: 'amicable',
          label: hasLegalCounsel(state)
            ? `Transaction amiable (-${amicableCost.toLocaleString('fr-FR')} € · Juliette)`
            : `Transaction amiable (-${amicableCost.toLocaleString('fr-FR')} €)`,
          tooltip: 'Évite l\'audience. Confidentialité préservée.',
          effects: [{ kind: 'treasury', amount: -amicableCost }],
        },
        {
          id: 'negotiate',
          label: hasLegalCounsel(state) ? `Médiation conventionnelle (Juliette)` : `Médiation conventionnelle`,
          tooltip: hasLegalCounsel(state)
            ? `Juliette obtient une réduction. ${Math.round(amicableCost * 0.65).toLocaleString('fr-FR')} €.`
            : 'Issue incertaine.',
          effects: [
            {
              kind: 'treasury',
              amount: hasLegalCounsel(state) ? -Math.round(amicableCost * 0.65) : -Math.round(amicableCost * 1.15),
            },
          ],
        },
        {
          id: 'ignore',
          label: `Audience contradictoire (-${ignoreCost.toLocaleString('fr-FR')} €)`,
          tooltip: 'Risque d\'indemnité majorée. -5 réputation employeur.',
          effects: [
            { kind: 'treasury', amount: -ignoreCost },
            { kind: 'reputation', amount: -5 },
          ],
        },
      ],
      metadata: { exposureMin, exposureMax, deadlineDays: 60 },
    });
  },
};

/* ─── Template Douanes Export — Procédure CMR/T-Forms ─────────────────── */

const TPL_DOUANES = {
  id: 'legal-douanes-export',
  type: 'douanes_export',
  cooldownDays: 540,
  probability: 0.004,
  condition: (state) => {
    // Au moins 1 contrat actif ou lifetime avec spé Export EU.
    const hasExportContracts = (state.activeContracts || []).some(
      c => (c.specialty || c.category || '').toLowerCase().includes('export'),
    );
    return hasExportContracts || (state.contracts_completed_lifetime || 0) >= 20;
  },
  build: (state, rng = Math.random) => {
    const fineMult = getLegalFineMultiplier(state);
    const exposureMin = Math.round(2500 * fineMult);
    const exposureMax = Math.round(14000 * fineMult);
    const amicableCost = pickAmount(exposureMin, exposureMin + 1500, rng);
    const ignoreCost = pickAmount(exposureMax, exposureMax + 2000, rng);
    return buildLegalMessage({
      type: 'douanes_export',
      authority: 'Direction Régionale des Douanes',
      subject: 'Procédure de redressement douanier — Export EU',
      body: `Madame, Monsieur,\n\nDans le cadre du contrôle a posteriori des **opérations d'exportation intracommunautaire**, nous relevons des anomalies sur vos déclarations T-Form et lettres de voiture CMR :\n\n• Codes Incoterms incomplets sur 3 expéditions\n• Différences de poids entre déclaration et pesée\n• Justificatifs d'autoliquidation TVA absents\n\nFondement : **Code des Douanes communautaire (CDC) art. 78** et règlement UE 952/2013.\n\n**Exposition :** ${exposureMin.toLocaleString('fr-FR')} – ${exposureMax.toLocaleString('fr-FR')} €.\n**Délai :** 45 jours.\n\n— Direction Régionale des Douanes`,
      choices: [
        {
          id: 'amicable',
          label: `Régularisation documentaire (-${amicableCost.toLocaleString('fr-FR')} €)`,
          tooltip: 'Dossier régularisé sous 45 jours.',
          effects: [{ kind: 'treasury', amount: -amicableCost }],
        },
        {
          id: 'negotiate',
          label: hasLegalCounsel(state) ? `Réclamation contentieuse (Juliette)` : `Réclamation contentieuse`,
          tooltip: hasLegalCounsel(state)
            ? `Juliette monte un dossier de défense. Coût ${Math.round(amicableCost * 0.55).toLocaleString('fr-FR')} €.`
            : 'Contentieux douanier complexe sans avocat.',
          effects: [
            {
              kind: 'treasury',
              amount: hasLegalCounsel(state) ? -Math.round(amicableCost * 0.55) : -Math.round(amicableCost * 1.25),
            },
          ],
        },
        {
          id: 'ignore',
          label: `Ignorer (-${ignoreCost.toLocaleString('fr-FR')} € + ITV)`,
          tooltip: 'Interdiction temporaire de transport intracommunautaire — pertes contrats.',
          effects: [
            { kind: 'treasury', amount: -ignoreCost },
            { kind: 'reputation', amount: -3 },
          ],
        },
      ],
      metadata: { exposureMin, exposureMax, deadlineDays: 45 },
    });
  },
};

/* ─── Template Litige client — Impayé > 60j ────────────────────────────── */

const TPL_LITIGE_CLIENT = {
  id: 'legal-litige-client',
  type: 'litige_client',
  cooldownDays: 240,
  probability: 0.008,
  condition: (state) => (state.unpaidInvoicesCount || 0) >= 1 || (state.contracts_completed_lifetime || 0) >= 30,
  build: (state, rng = Math.random) => {
    const fineMult = getLegalFineMultiplier(state);
    // ICI le joueur a un GAIN potentiel (recouvrement). On inverse la logique.
    const recoverable = pickAmount(8000, 25000, rng);
    const legalFees = Math.round(2500 * fineMult);
    return buildLegalMessage({
      type: 'litige_client',
      authority: 'Cabinet Hermès & Associés',
      subject: 'Litige client — Impayé en recouvrement',
      body: `Madame, Monsieur,\n\nVous nous avez sollicités pour le recouvrement d'une **créance impayée de ${recoverable.toLocaleString('fr-FR')} €** auprès d'un de vos clients.\n\nLa relance amiable n'a pas abouti après 90 jours. Le débiteur invoque un litige sur la qualité de la prestation.\n\nOptions :\n• Médiation conventionnelle (issue rapide, montant variable)\n• Procédure judiciaire (Tribunal de commerce, 6-18 mois)\n• Abandon de créance (passe en charge exceptionnelle)\n\n— Me Hermès, Recouvrement contentieux`,
      choices: [
        {
          id: 'amicable',
          label: `Médiation (récupère ~${Math.round(recoverable * 0.6).toLocaleString('fr-FR')} €)`,
          tooltip: `Frais ${legalFees.toLocaleString('fr-FR')} €. Net ${Math.round(recoverable * 0.6 - legalFees).toLocaleString('fr-FR')} €.`,
          effects: [{ kind: 'treasury', amount: Math.round(recoverable * 0.6) - legalFees }],
        },
        {
          id: 'negotiate',
          label: hasLegalCounsel(state)
            ? `Procédure judiciaire (Juliette)`
            : `Procédure judiciaire`,
          tooltip: hasLegalCounsel(state)
            ? `Juliette monte le dossier. 75 % de chance de récupérer ${recoverable.toLocaleString('fr-FR')} € net de frais.`
            : 'Procédure longue et coûteuse sans avocat dédié.',
          effects: [
            {
              kind: 'treasury',
              amount: hasLegalCounsel(state)
                ? Math.round(recoverable * 0.75) - Math.round(legalFees * 2)
                : Math.round(recoverable * 0.45) - Math.round(legalFees * 3),
            },
            { kind: 'reputation', amount: hasLegalCounsel(state) ? +2 : -1 },
          ],
        },
        {
          id: 'ignore',
          label: `Abandon de créance (-${recoverable.toLocaleString('fr-FR')} €)`,
          tooltip: 'Aucun frais mais perte sèche. Passe en charge exceptionnelle.',
          effects: [{ kind: 'treasury', amount: 0 }], // déjà comptabilisé en CA non payé
        },
      ],
      metadata: { exposureMin: 0, exposureMax: recoverable, deadlineDays: 60 },
    });
  },
};

/* ─── Liste exportée des templates ────────────────────────────────────── */

export const LEGAL_PROCEDURE_TEMPLATES = [
  TPL_URSSAF,
  TPL_DREAL,
  TPL_PRUDHOMMES,
  TPL_DOUANES,
  TPL_LITIGE_CLIENT,
];

/** Helper de test : retrouve un template par id. */
export function getLegalProcedureTemplate(id) {
  return LEGAL_PROCEDURE_TEMPLATES.find(t => t.id === id) || null;
}
